/**
 * Types of the site tree: the one place that says where each public page sits.
 * See tree.ts for the data and queries.ts for what is derived from it.
 */

/**
 * Where a page sits in the consultant's structure.
 * - 1: overview hub (Maßnahmen, Leistungen, Für Vorhabenträger, …)
 * - 2: money page a hub lists (Ökopunkte, Flächenvermittlung, …)
 * - 3: article below a level-2 page (CEF-Maßnahmen, …)
 * - "legacy": an existing article placed in the structure
 * - "utility": home, legal and company pages outside the structure
 */
type SiteLevel = 1 | 2 | 3 | "legacy" | "utility";

/** A crumb between "Startseite" and the page itself. */
export interface TrailStep {
  path: string;
  /** Label override for this crumb; defaults to the target node's `crumb`. */
  crumb?: string;
}

/** Live figures a hub card can show under a page (rendered only when known). */
export type CardFact = "offerCount" | "listedHectares";

/** The icons a hub card's tile can show; HubCardIcon maps each name to its glyph. */
export const CARD_ICONS = [
  "arrowLeftRight",
  "bookOpen",
  "briefcase",
  "clipboardList",
  "coins",
  "fileBarChart",
  "fileText",
  "hardHat",
  "key",
  "landmark",
  "leaf",
  "map",
  "mapPin",
  "newspaper",
  "scale",
  "search",
  "send",
  "shieldCheck",
  "sun",
  "zap",
] as const;

export type CardIcon = (typeof CARD_ICONS)[number];

/** A link on a hub card: a page of the tree or a funnel route, under its own label. */
export interface CardLink {
  path: string;
  label: string;
}

/** How a page looks as a card in a hub's grid. */
interface HubCardSpec {
  icon: CardIcon;
  /** The card's title; defaults to the crumb. */
  title?: string;
  /** "dark": the hub's featured card, which ends in a button instead of links. */
  tone?: "dark";
  /** The dark card's button. */
  cta?: CardLink;
  /** Curated links; without them the card lists the page's level-3 children. */
  links?: readonly CardLink[];
}

export interface SiteNode {
  /** The page's URL path, always a `routes.*` value. */
  path: string;
  /** Short label used in breadcrumbs (and as the hub card title). */
  crumb: string;
  level: SiteLevel;
  /** Breadcrumb parent. Omitted: the page sits directly under "Startseite". */
  parent?: string;
  /**
   * Explicit crumbs between "Startseite" and the page, for Wissen articles
   * whose breadcrumb mirrors the transactional path ("Wissen › Erneuerbare
   * Energien › Photovoltaik › …"). Takes precedence over `parent`.
   */
  trail?: readonly TrailStep[];
  /**
   * Further hubs whose card grid lists this page. The breadcrumb parent and
   * the menus a page appears in differ in the sheet: /oekopunkte-kaufen is
   * in the Vorhabenträger menu, but its breadcrumb runs through Ökopunkte.
   */
  hubs?: readonly string[];
  /**
   * The level-2 page this page links up to. Undefined: the nearest level-2
   * ancestor. `null`: no link-up.
   */
  upTo?: string | null;
  /** How this page looks as a hub card (level 2 only). */
  card?: HubCardSpec;
  /** A live figure shown on this page's hub card. */
  cardFact?: CardFact;
  /**
   * Hubs only: the order of the card grid. Must name exactly the pages that
   * sit under the hub or list it in `hubs` (site-tree.test.ts); without it the
   * cards follow tree order.
   */
  cardOrder?: readonly string[];
}

/** One rendered breadcrumb. The last one is the current page. */
export interface Crumb {
  name: string;
  path: string;
}
